import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';

import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';

import {
  openCustomerRequestSidePanelAction,
  selectContextCustomerRequestInfo,
  setContextCustomerRequestAction,
} from 'saga/context/customerRequest/ducks';
import {
  ContextExternalActionTypes,
  selectContextExternalUser,
  setContextExternalUserActions,
  updateEntityAccessesActions,
} from 'saga/context/external/ducks';
import { SharedObjectsData } from 'generated/sharedObjects/models';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { environment } from 'environments/environment';
import { isComingFromLFR } from 'saga/appHandlers/ducks';
import { selectContextCustomerInfo, setContextCustomer } from 'saga/context/customer/ducks';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { setContextOrderActions } from 'saga/context/order/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { selectContextInternalUser, setExternalScreenFilterAction } from 'saga/context/internal/ducks';
import { CustomerRequestInfo } from 'models/context/CustomerRequestInfo';
import { setContextPartnerContractActions } from 'saga/context/partnerContract/ducks';
import { setContextUserPreferencesActions } from 'saga/context/userPreferences/ducks';
import { SetContextExternalUserRequest } from 'models/SetContextExternalUserRequest';
import { EntityAccessAssigned, ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';

/* STATE */
export interface SharedObjectsState {
  data: SharedObjectsData | null;
}

/* ACTION TYPES */
export enum SharedObjectsActionTypes {
  GetSession = '@@SharedObjects/GET_SESSION',
  SynchronizeContext = '@@SharedObjects/SYNCHRONIZE_CONTEXT',
}

/* ACTIONS */
export const GetSessionAction = createApiActionCreators(SharedObjectsActionTypes.GetSession);
export const SynchronizeContextAction = createApiActionCreators(SharedObjectsActionTypes.SynchronizeContext);

/* REDUCERS */
const initialState: SharedObjectsState = {
  data: null,
};

const data = createReducer(initialState.data, {
  [SharedObjectsActionTypes.GetSession]: {
    [RequestActionTypes.SUCCESS]: (state: SharedObjectsData, payload: SharedObjectsData) => {
      return payload || null;
    },
  },
});

export default combineReducers<SharedObjectsState>({
  data,
});

/* SELECTORS */
const selectSharedObjects = (state: AppState) => state.sharedObjects;

export const selectSharedObjectsData = (state: AppState) => selectSharedObjects(state).data;

/* SAGAS */
function* getSession() {
  const request: ApiClientRequest = {
    apiCall: call(api.getSession, yield getApiUrl(environment.urlConfig.sharedObjects)),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(GetSessionAction.success(resp.data));

    return resp.data;
  } else {
    yield put(GetSessionAction.failure());
  }

  return null;
}

function* synchronizeContextSetContextCustomer() {
  const session: SharedObjectsData = yield select(selectSharedObjectsData);

  if (!session) {
    return;
  }

  // EA: setContextCustomer
  const forceUpdate: boolean = yield select(isComingFromLFR);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);

  if (session.customer?.cuRefNo !== contextCustomer?.cuRefNo) {
    yield call(setContextCustomer, {
      payload: {
        cuRefNo: session.customer?.cuRefNo,
        skipSharedObjectsSession: true,
        forceUpdate: forceUpdate,
      },
    });
  }

  yield call(finalizeSynchronizeContext);
}

function* finalizeSynchronizeContext() {
  const session: SharedObjectsData = yield select(selectSharedObjectsData);

  if (!session) {
    return;
  }

  // EA: setContextOrder
  if (session.orderBasket) {
    yield put(
      setContextOrderActions({
        orderRefNo: session.orderBasket.orderRefNo,
        itemsCount: session.orderBasket.itemCount,
      })
    );
  }

  // EA: Set screenFilter
  const isInternalUser: boolean = yield select(selectIsUserInternal);

  if (isInternalUser) {
    yield put(setExternalScreenFilterAction.success(!!session.internalUser?.preferences?.externalScreenFilter));
  }

  // EA: Set CR reference
  const contextInternalUser: InternalUser | null = yield select(selectContextInternalUser);
  const screenFilterIsInternal: boolean = contextInternalUser?.screenFilter === 'internal';
  const crInfo: CustomerRequestInfo | null = yield select(selectContextCustomerRequestInfo);
  const sessionCrRefNo: any = crInfo?.crRefNo;

  if (session.customerRequest?.crRefNo && screenFilterIsInternal) {
    yield put(openCustomerRequestSidePanelAction(session.customerRequest?.crRefNo));
  }

  if (session.customerRequest?.crRefNo !== sessionCrRefNo) {
    yield put(
      setContextCustomerRequestAction({
        crRefNo: session.customerRequest?.crRefNo,
      })
    );
  } else if (session.customerRequest?.crRefNo === null) {
    yield put(
      setContextCustomerRequestAction({
        crRefNo: null,
      })
    );
  }

  /*
   * EA:Set partnerContract
   * TODO: write code for setContext partnerContract loading, but just if is synchronize partnerContract
   */

  yield put(
    setContextPartnerContractActions({
      marketingOfferCode: session.partnerContract?.marketingOfferCode ?? '',
      marketingOfferName: session.partnerContract?.marketingOfferName ?? '',
      obligationRefNo: session.partnerContract?.obligationRefNo ?? '',
    })
  );

  yield put(SynchronizeContextAction.success());
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.ContextManagement.synchronizeContext.synchronizeContext
 */
function* synchronizeContext() {
  const session: SharedObjectsData = yield call(getSession);

  if (!session) {
    yield put(SynchronizeContextAction.success());

    return;
  }

  // EA: setContextUserPreferences
  if (session?.generalLoginData?.userPreferences) {
    yield put(
      setContextUserPreferencesActions.request({
        locale: session.generalLoginData.userPreferences.locale,
        darkMode: session.generalLoginData.userPreferences.darkMode,
        vatPrice: session.generalLoginData.userPreferences.vatPrice,
        skipSharedObjectsSession: true,
      })
    );
  }

  // EA: setContextExternalUser
  const contextExtUser: ExternalUser = yield select(selectContextExternalUser);
  const contextIntUser: InternalUser = yield select(selectContextInternalUser);

  if (
    contextIntUser &&
    session.externalUser &&
    (!contextExtUser ||
      session.externalUser.cpRefNo !== contextExtUser.cpRefNo ||
      session.externalUser.verifiedUser !== contextExtUser.isVerified)
  ) {
    const payload: SetContextExternalUserRequest = {
      koId: session.externalUser.cpRefNo,
      skipSharedObjectsSession: true,
    };

    yield put(setContextExternalUserActions.request(payload));
    yield takeLatest(
      createActionType(ContextExternalActionTypes.SetContextExternalUser, RequestActionTypes.SUCCESS),
      synchronizeContextSetContextCustomer
    );
  } else {
    /*
     * setContextExternalUser wasn't performed, so it is possible continue directly
     * EA: Set temporary verified access
     */
    const contextExtUser: ExternalUser = yield select(selectContextExternalUser);

    if (contextExtUser && contextExtUser.entityAccesses) {
      const entityAccesses = contextExtUser.entityAccesses;

      entityAccesses.forEach((access: EntityAccessAssigned) => {
        access.isVerified = false;
      });
      session.untrustedEntities?.forEach((untrustedEntity) => {
        const index = entityAccesses.findIndex((access: EntityAccessAssigned) => {
          return access.entityType === untrustedEntity.entityType && access.entityRefNo === untrustedEntity.entityRefNo;
        });

        if (index !== -1) {
          entityAccesses[index].isVerified = true;
        }
      });
      yield put(updateEntityAccessesActions.success(entityAccesses));
    }

    yield call(synchronizeContextSetContextCustomer);
  }
}

/* EXPORT */
export function* sharedObjectsSaga() {
  yield takeLatest(createActionType(SharedObjectsActionTypes.GetSession, RequestActionTypes.REQUEST), getSession);

  yield takeLatest(
    createActionType(SharedObjectsActionTypes.SynchronizeContext, RequestActionTypes.REQUEST),
    synchronizeContext
  );
}
